import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
  Trophy,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  usePublishedEvents,
  EVENT_TYPE_LABELS,
} from '@/features/events/useEvents';
import { fadeUp, slideLeft, scaleIn, staggerContainer } from '@/lib/animations';
import { FOCAL } from '@/lib/imageSlots';

const ICONS: Record<string, typeof Trophy> = {
  'tournament-crib': Trophy,
  show: Sparkles,
  tasting: Sparkles,
  special: Sparkles,
  other: Calendar,
};

/**
 * Section sur la page d'accueil qui met en avant le prochain événement
 * publié à venir. Affiche la photo, le titre, la description, la date,
 * et un bouton vers /evenements pour s'inscrire.
 *
 * Si aucun événement à venir : retourne null (la section disparaît).
 */
export default function NextEventHighlight() {
  const { data: events = [], isLoading } = usePublishedEvents();
  const prefersReducedMotion = useReducedMotion();

  if (isLoading) return null;
  if (events.length === 0) return null;

  const next = events[0];
  const Icon = ICONS[next.event_type] ?? Calendar;
  const startDate = new Date(next.starts_at);
  const dateLabel = format(startDate, 'EEEE d MMMM', { locale: fr });
  const dateLabelCap = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
  const timeLabel = format(startDate, 'HH\'h\'mm', { locale: fr });
  // Countdown affiché si l'événement est dans moins de 7 jours.
  const daysUntil = differenceInDays(startDate, new Date());
  const showCountdown = daysUntil >= 0 && daysUntil < 7;
  const countdownLabel = showCountdown
    ? formatDistanceToNow(startDate, { locale: fr, addSuffix: true })
    : null;

  return (
    <motion.section
      className="relative overflow-hidden border-y border-[#D4A574]/20 bg-gradient-to-b from-[#D4A574]/5 to-transparent"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer(0.12, 0.05)}
    >
      {/* Halo doré pulsant en arrière-plan (sauf si prefers-reduced-motion) */}
      <motion.div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-[#D4A574]/5 blur-3xl"
        animate={
          prefersReducedMotion
            ? undefined
            : { scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }
        }
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-12 md:py-16">
        <motion.div
          variants={fadeUp}
          className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#D4A574] mb-6 justify-center md:justify-start flex-wrap"
        >
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            Prochain événement
          </span>
          {showCountdown && countdownLabel && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D4A574]/15 border border-[#D4A574]/30 normal-case tracking-normal text-[11px] font-medium">
              <motion.span
                className="h-1.5 w-1.5 rounded-full bg-[#D4A574]"
                animate={
                  prefersReducedMotion
                    ? undefined
                    : { opacity: [1, 0.3, 1], scale: [1, 1.3, 1] }
                }
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              <Clock className="h-3 w-3" />
              {countdownLabel}
            </span>
          )}
        </motion.div>

        <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center">
          {/* Photo */}
          <motion.div variants={slideLeft}>
            {next.cover_image_url ? (
              <Link
                to="/evenements"
                className="block shrink-0 mx-auto md:mx-0 relative group"
              >
                <motion.img
                  src={next.cover_image_url}
                  alt={next.title}
                  loading="lazy"
                  whileHover={{ scale: 1.03, rotate: -1 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  style={{ objectPosition: FOCAL.people }}
                  className="w-full md:w-64 aspect-square rounded-2xl object-cover border border-[#D4A574]/30 shadow-2xl shadow-[#D4A574]/10"
                />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-transparent group-hover:ring-[#D4A574]/50 transition-all duration-500 pointer-events-none" />
              </Link>
            ) : (
              <div className="w-full md:w-64 aspect-square rounded-2xl bg-[#0A0A0A] border border-[#D4A574]/30 flex items-center justify-center shrink-0 mx-auto md:mx-0">
                <Icon className="h-20 w-20 text-[#D4A574]/40" />
              </div>
            )}
          </motion.div>

          {/* Infos */}
          <motion.div
            variants={fadeUp}
            className="min-w-0 space-y-3 text-center md:text-left"
          >
            <Badge
              variant="outline"
              className="border-[#D4A574]/40 text-[#D4A574] bg-transparent"
            >
              <Icon className="h-3 w-3 mr-1" />
              {EVENT_TYPE_LABELS[next.event_type]}
            </Badge>
            <h2 className="font-display text-3xl md:text-5xl text-[#E8E0D6] leading-tight">
              {next.title}
            </h2>
            <div className="flex items-center gap-4 text-sm text-[#D4A574] tabular-nums justify-center md:justify-start flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {dateLabelCap} · {timeLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 text-neutral-400">
                <MapPin className="h-4 w-4" />
                {next.location}
              </span>
            </div>
            {next.description && (
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed line-clamp-3 max-w-2xl">
                {next.description}
              </p>
            )}
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={scaleIn}
            className="flex justify-center md:justify-end"
          >
            <Link
              to="/evenements"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#D4A574] text-[#0A0A0A] font-medium text-sm tracking-wide hover:bg-[#C9A36B] transition-all whitespace-nowrap shadow-xl shadow-[#D4A574]/20"
            >
              M'inscrire
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
